import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'

import { useAjaxContext } from '../../../../core/ajax'
import { getFormattedTime, dataToXLS } from '../../../../core/utils'
import { feedback } from '../../../../core/feedback'
import { ThemeButton, useModalContext } from '../../../../ui'
import {
  MARKETER_POTENTIAL_TABLE_COLUMNS_KEY,
  EXPORT_POTENTIAL_TABLE_COLUMNS as columns,
} from '../../constants'
import { ExportNotEmptyDialog, ExportSomethingDialog } from '../dialogs'
import { marketerPotentialsDataPreparation } from '../../logics/marketer-potentials.logics'
import { useExportToXLSCalls } from '../../hooks'

export const ExportPotentialsToXLSBtn = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const email = useSelector((state) => state.global.email)
  const marketerId = useSelector((state) => state.global.comercializadora_id)
  const selectedRows = useSelector((state) => state.marketerPotential.selectedRows)
  const search = useSelector((state) => state.marketerPotential.search)
  const pagination = useSelector((state) => state.marketerPotential.pagination)
  const combos = useSelector((state) => state.combos)
  const { get } = useAjaxContext()
  const { requestNewExportByEmail } = useExportToXLSCalls()
  const {
    actions: { open },
  } = useModalContext()

  const [loading, setLoading] = useState(false)

  const exportUrl = `/comercializadoras/${marketerId}/tabla_potenciales`
  const exportUrlAsync = `/extracciones/by_email_instalador`

  const mainExport = async (data) => {
    const fileName = `potenciales_comercializadora_${getFormattedTime()}.xlsx`
    await dataToXLS(data, columns, combos, intl, MARKETER_POTENTIAL_TABLE_COLUMNS_KEY, fileName)
  }

  const handleExport = async () => {
    if (!selectedRows?.length) {
      open({ Component: ExportSomethingDialog })
      return
    }

    setLoading(true)
    try {
      await mainExport(selectedRows)
    } catch (error) {
      console.error('Error al exportar filas seleccionadas:', error)
      feedback('error', formatMessage({ id: 'global.error.generic' }))
    } finally {
      setLoading(false)
    }
  }

  const handleExportAll = async () => {
    const { total_potenciales } = pagination
    if (!total_potenciales) {
      open({ Component: ExportNotEmptyDialog })
      return
    }

    setLoading(true)
    try {
      const { _num_x_pagina, _pagina, ...otherSearch } = search

      if (total_potenciales > 500) {
        try {
          await requestNewExportByEmail(email)
          feedback(
            'info',
            formatMessage({ id: 'pages.marketer.potential.exportToXLS.dialogs.large.text' })
          )
        } catch (code) {
          if (code === 'already started') {
            feedback(
              'warning',
              formatMessage(
                { id: 'pages.marketer.potential.exportToXLS.dialogs.running.export.text' },
                { email }
              )
            )
          } else {
            await get(exportUrlAsync, { params: { ...otherSearch } })
            feedback(
              'info',
              formatMessage({ id: 'pages.marketer.potential.exportToXLS.dialogs.large.text' })
            )
          }
        }
      } else {
        const { data } = await get(exportUrl, {
          params: { ...otherSearch, _num_x_pagina: total_potenciales, _pagina: '1' },
        })

        if (!data?.potenciales) {
          throw new Error('No se recibieron datos válidos del servidor.')
        }

        const recievedData = marketerPotentialsDataPreparation(columns, data.potenciales, { intl })
        await mainExport(recievedData)
      }
    } catch (error) {
      console.error('Error durante la exportación:', error)
      const message =
        error?.response?.data?.message || formatMessage({ id: 'global.error.generic' })
      feedback('error', message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <ThemeButton
        color="primary"
        labelM="sm"
        onClick={handleExport}
        style={{ marginRight: '1em' }}
        loading={loading}
        disabled={loading}
      >
        {formatMessage({ id: 'global.exportSelected' })} ({selectedRows.length})
      </ThemeButton>
      <ThemeButton
        color="primary"
        labelM="sm"
        loading={loading}
        onClick={handleExportAll}
        disabled={loading}
      >
        {formatMessage({ id: 'global.exportAll' })}
      </ThemeButton>
    </>
  )
}
